import { ReactNode } from "react";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import { SeatingStateProvider } from "@shared/modules/seating/contexts/useSeatingState";
import { common } from "@shared/constants";
import EnhancedCartSummary from "@app-contexts/public/features/basket/components/EnhancedCartSummary/EnhancedCartSummary.web";
import CartSummaryModal from "@app-contexts/public/features/basket/components/CartSummaryModal/CartSummaryModal.web";
import type { EnhancedCartSummaryDisplayMode } from "@app-contexts/public/features/basket/components/EnhancedCartSummary/EnhancedCartSummary.props";
import type { EnhancedCartAnalyticsSource } from "@app-contexts/public/features/basket/components/EnhancedCartSummary/EnhancedCartSummaryAnalyticsContext";
import type { NestedPublicVenueSerializer } from "@app-contexts/public/features/event-detail/data/types/events";

export type CartSummaryContainerRenderProps = {
  onOpen: () => void;
  isOpen: boolean;
};

export type CartSummaryContainerProps = {
  displayMode?: EnhancedCartSummaryDisplayMode;
  onCheckout: () => void;
  renderButton: (props: CartSummaryContainerRenderProps) => ReactNode;
  venueId?: number;
  venue?: NestedPublicVenueSerializer;
  holdLink?: string;
  dynamicHoldParams?: { purchase_platform: string; payment_type: string };
  analyticsSource?: EnhancedCartAnalyticsSource;
};

export default function CartSummaryContainer({
  displayMode = "drawer",
  onCheckout,
  renderButton,
  venueId,
  venue,
  holdLink,
  dynamicHoldParams,
  analyticsSource,
}: CartSummaryContainerProps): JSX.Element {
  const { isFlagActive } = useDjangoWaffle();
  const shouldUseEnhancedCart = isFlagActive("enhanced_cart_summary", venueId);

  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <SeatingStateProvider
        isOnSeatingMapPage={false}
        basketType={common.DataProvider.Public}
      >
        {shouldUseEnhancedCart ? (
          <EnhancedCartSummary
            isOpen={isOpen}
            onClose={onClose}
            displayMode={displayMode}
            onCheckout={onCheckout}
            dynamicHoldParams={dynamicHoldParams}
            analyticsSource={analyticsSource}
            venue={venue}
            venueId={venueId}
            sourceStep="enhanced_cart_summary"
          />
        ) : (
          <CartSummaryModal
            isOpen={isOpen}
            onClose={onClose}
            venue={venue}
            holdLink={holdLink}
            dynamicHoldParams={dynamicHoldParams}
          />
        )}
      </SeatingStateProvider>
      {renderButton({ onOpen, isOpen })}
    </>
  );
}
